import { useMemo } from 'react';
import { useForm } from '@common-fe/common-fe';
import { yupResolver } from '@hookform/resolvers/yup';
import toString from 'lodash/toString';

import { ServicePaymentType } from '@/modules/HealthPlan/HealthPlan.types';
import { HealthPlanRenewal, RenewalPaymentRule } from '@/modules/Renewals/renewals.types';

import { buildEditAmountsSchema } from '../EditAmountsModal.resolver';
import { EditAmountsFormValues } from '../EditAmountsModal.types';

const getRuleAmount = (rule: RenewalPaymentRule, paymentType?: string): string => {
  if (paymentType === ServicePaymentType.PERCENTAGE) {
    return rule.percentage != null ? `${Math.floor(rule.percentage * 100)}` : '';
  }
  return toString(rule.maxAmount);
};

const buildDefaultValues = (healthPlan: HealthPlanRenewal): EditAmountsFormValues => ({
  tiers: (healthPlan.processingTiers || []).map((tier) => ({
    services: (tier.serviceConfigs || []).map((service) => ({
      paymentRules: (service.paymentRules || []).map((rule) => ({
        amount: getRuleAmount(rule, service.paymentType),
      })),
    })),
  })),
  coverageLevels: (healthPlan.deductibleConfigs || []).map((config) => ({
    triggers: (config.triggerConfigs || []).map((trigger) => ({
      amount: toString(trigger.thresholdAmount),
    })),
    plans: (config.autoFundingConfigs || []).map((funding) => ({
      perPersonSpendLimit: toString(funding.perPersonSpendLimitAmount),
      autoFundingAmount:
        funding.autoEnrollmentAmount != null ? toString(funding.autoEnrollmentAmount) : undefined,
      categorySpendLimits: (config.categorySpendLimits || [])
        .filter((csl) => csl.planId === funding.planId)
        .map((csl) => ({
          amount: toString(csl.categorySpendLimit),
        })),
    })),
  })),
});

const useEditAmountsForm = (healthPlan: HealthPlanRenewal) => {
  const schema = useMemo(() => {
    const processingTiers = healthPlan.processingTiers || [];
    const deductibleConfigs = healthPlan.deductibleConfigs || [];
    return buildEditAmountsSchema(processingTiers, deductibleConfigs);
  }, [healthPlan.processingTiers, healthPlan.deductibleConfigs]);

  const methods = useForm<EditAmountsFormValues>({
    defaultValues: buildDefaultValues(healthPlan),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(schema) as any,
  });

  return { methods, buildDefaultValues };
};

export default useEditAmountsForm;
